import React, { useEffect, useState } from 'react';

// 年度を後から直すパネル【2026-09-17】
//
//   mode='batch' … 名簿の取り込み1回ぶん（/api/roster-imports/:id）
//   mode='class' … 授業1つ（/api/roster-imports/classes/:id/year）。保留を解くときもこれ
//
// いきなり変えない。まず dryRun で「何がどうなるか」を出し、見てから確定する。
// 正しい年度に同じ名前の授業があるときは、必ず目立つ警告を出して
//   「その授業に合流する」か「その授業も怪しいので保留にする」かを選んでもらう。
// 選ぶたびに計画を取り直す（合流を選ぶと、出席記録の都合で止まることがあるため）。

const ACTION = {
  none:     { label: '変更なし',                         color: '#6b7280' },
  skip:     { label: '動かさない',                       color: '#6b7280' },
  relabel:  { label: '授業ごと年度を変える',             color: '#166534' },
  split:    { label: '新しい年度に授業を作り、学生を移す', color: '#1d4ed8' },
  merge:    { label: '既存の授業に合流する',             color: '#1d4ed8' },
  conflict: { label: '要確認：同じ名前の授業があります', color: '#b91c1c' },
  blocked:  { label: '動かせません',                     color: '#b45309' },
};

const HOW = {
  created: 'この取り込みで作成',
  matched: '名前で既存の授業に追加',
  mapped:  '取り込み時に授業を指定',
  forced:  '取り込み時に授業を指定',
};

function yearLabel(x) {
  if (!x) return '';
  if (x.onHold) return `保留中（${x.academicYear ?? '年度なし'}年度${x.termName || ''}）`;
  return `${x.academicYear ?? '年度なし'}${x.academicYear ? '年度' : ''}${x.termName || ''}`;
}

export default function YearChangePanel({
  mode, targetId, title, initialYear, initialTermId, onDone, onCancel, modal = false,
}) {
  const [years, setYears]   = useState([]);
  const [terms, setTerms]   = useState([]);
  const [year, setYear]     = useState(initialYear ? String(initialYear) : '');
  const [termId, setTermId] = useState(initialTermId ? String(initialTermId) : '');
  const [plan, setPlan]     = useState(null);
  const [resolutions, setResolutions] = useState({});   // { 相手の授業ID: 'merge' | 'hold' }
  const [skips, setSkips]   = useState([]);             // 動かさない授業ID
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState('');
  const [holdNote, setHoldNote] = useState('');

  const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });
  const url = mode === 'batch'
    ? '/api/roster-imports/' + targetId
    : '/api/roster-imports/classes/' + targetId + '/year';

  useEffect(() => {
    fetch('/api/terms', { headers: auth() })
      .then(r => r.json())
      .then(d => {
        if (!d.success) return;
        setTerms(d.terms || []);
        // 今の値が選択肢に無い（古い年度など）ときも選べるように足しておく
        const ys = new Set((d.yearOptions || []).map(Number));
        if (initialYear) ys.add(Number(initialYear));
        setYears([...ys].sort((a, b) => b - a));
        if (!initialYear && d.defaults) setYear(String(d.defaults.academicYear ?? ''));
        if (!initialTermId && d.defaults && d.defaults.termId) setTermId(String(d.defaults.termId));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 年度・学期を変えたら、前の計画と選択は捨てる（相手の授業が変わるため）
  useEffect(() => { setPlan(null); setResolutions({}); setSkips([]); }, [year, termId]);

  const send = async (dryRun, res = resolutions, sk = skips) => {
    setBusy(true); setErr('');
    try {
      const r = await fetch(url, {
        method: 'PATCH',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          academicYear: year, termId, dryRun,
          resolutions: res, skips: sk,
        }),
      });
      const d = await r.json();
      if (d.plan) setPlan(d.plan);
      if (!d.success) {
        if (d.code === 'YEAR_CONFLICT' || d.code === 'YEAR_BLOCKED') {
          setErr(d.message);
          return;
        }
        throw new Error(d.message || '変更に失敗しました');
      }
      if (!dryRun && onDone) onDone(d.message);
    } catch (e) { setErr(e.message); }
    finally     { setBusy(false); }
  };

  const choose = (otherId, value) => {
    const next = { ...resolutions, [String(otherId)]: value };
    setResolutions(next);
    send(true, next, skips);
  };
  const toggleSkip = (classId) => {
    const next = skips.includes(classId) ? skips.filter(x => x !== classId) : [...skips, classId];
    setSkips(next);
    send(true, resolutions, next);
  };

  const holdThis = async () => {
    if (!window.confirm('この授業を「年度保留」にします。年度の一覧から外れ、「保留中」にだけ出ます。\n' +
                        '打刻や今日の授業には影響しません。よろしいですか？')) return;
    setBusy(true); setErr('');
    try {
      const r = await fetch('/api/roster-imports/classes/' + targetId + '/hold', {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: holdNote }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || '保留にできませんでした');
      if (onDone) onDone(d.message);
    } catch (e) { setErr(e.message); }
    finally     { setBusy(false); }
  };

  const unresolved = (plan || []).filter(s => s.action === 'conflict').length;
  const changes = (plan || []).filter(s => ['relabel', 'split', 'merge'].includes(s.action)).length;
  const canApply = plan && unresolved === 0 && (changes > 0 || (plan || []).some(s => s.holdOther));

  const body = (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
                  padding: '1rem', maxWidth: 860, width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <strong style={{ fontSize: '1rem' }}>{title || '年度を直す'}</strong>
        <span style={{ flex: 1 }} />
        {onCancel && <button className="btn-secondary" onClick={onCancel} disabled={busy}>閉じる</button>}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', margin: '0.8rem 0' }}>
        <label style={{ fontSize: '0.85rem' }}>正しい年度</label>
        <select value={year} onChange={e => setYear(e.target.value)}>
          {years.length === 0 && <option value="">（読み込み中）</option>}
          {years.map(y => <option key={y} value={y}>{y}年度</option>)}
        </select>
        <select value={termId} onChange={e => setTermId(e.target.value)}>
          {terms.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button className="btn-secondary" onClick={() => send(true)} disabled={busy || !year}>
          {busy ? '確認中…' : 'どうなるか確認する'}
        </button>
      </div>

      {err && <div style={{ color: '#b91c1c', margin: '0.5rem 0', fontSize: '0.9rem' }}>{err}</div>}

      {plan && plan.length === 0 && (
        <p style={{ color: '#888' }}>この取り込みに紐づく授業が見つかりません。</p>
      )}

      {plan && plan.map(s => {
        const a = ACTION[s.action] || { label: s.action, color: '#333' };
        const isConflict = s.action === 'conflict' || (s.conflict && s.resolution);
        return (
          <div key={s.classId} style={{
            border: '1px solid ' + (s.action === 'conflict' ? '#fca5a5' : '#e5e7eb'),
            background: s.action === 'conflict' ? '#fef2f2' : '#fafafa',
            borderRadius: 8, padding: '0.6rem 0.8rem', marginBottom: '0.5rem',
          }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
              <strong>{s.className}</strong>
              {s.how && <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>{HOW[s.how] || s.how}</span>}
              {mode === 'batch' && typeof s.mine === 'number' && (
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>この取り込みの履修者 {s.mine}人</span>
              )}
              <span style={{ flex: 1 }} />
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: a.color }}>{a.label}</span>
            </div>
            <div style={{ fontSize: '0.82rem', color: '#374151', marginTop: '0.2rem' }}>
              {yearLabel(s.from)} → {s.to.academicYear}年度{s.to.termName || ''}
            </div>
            {s.reason && <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.2rem' }}>{s.reason}</div>}
            {(s.warnings || []).map((w, i) => (
              <div key={i} style={{ fontSize: '0.8rem', color: s.action === 'conflict' ? '#b91c1c' : '#b45309',
                                    marginTop: '0.2rem', fontWeight: s.action === 'conflict' ? 700 : 400 }}>
                ⚠ {w}
              </div>
            ))}

            {isConflict && s.conflict && (
              <div style={{ marginTop: '0.4rem', padding: '0.4rem 0.6rem', background: '#fff',
                            border: '1px dashed #f87171', borderRadius: 6, fontSize: '0.84rem' }}>
                <div style={{ marginBottom: '0.3rem' }}>
                  既にある授業：<strong>{s.conflict.name}</strong>（{yearLabel(s.conflict)}・
                  履修者 {s.conflict.enrolled}人・実施 {s.conflict.sessions}回
                  {s.conflict.archived ? '・畳んだ授業' : ''}）
                </div>
                <label style={{ display: 'block', cursor: 'pointer' }}>
                  <input type="radio" name={'res-' + s.classId}
                         checked={s.resolution === 'merge'}
                         onChange={() => choose(s.conflict.id, 'merge')} disabled={busy} />
                  {' '}この授業に合流する（同じ授業だった）
                </label>
                <label style={{ display: 'block', cursor: 'pointer' }}>
                  <input type="radio" name={'res-' + s.classId}
                         checked={s.resolution === 'hold'}
                         onChange={() => choose(s.conflict.id, 'hold')} disabled={busy} />
                  {' '}既にある授業も年度が怪しいので「保留」に移し、別々にしておく
                </label>
              </div>
            )}

            {mode === 'batch' && !['mapped', 'forced'].includes(s.how) && s.action !== 'none' && (
              <label style={{ display: 'block', fontSize: '0.78rem', color: '#6b7280', marginTop: '0.3rem' }}>
                <input type="checkbox" checked={skips.includes(s.classId)}
                       onChange={() => toggleSkip(s.classId)} disabled={busy} />
                {' '}この授業は動かさない
              </label>
            )}
          </div>
        );
      })}

      {plan && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginTop: '0.6rem' }}>
          <button className="btn-primary" onClick={() => send(false)} disabled={busy || !canApply}>
            この内容で変更する
          </button>
          {unresolved > 0 && (
            <span style={{ color: '#b91c1c', fontSize: '0.85rem', fontWeight: 700 }}>
              同じ名前の授業がある {unresolved}件 の扱いを選んでください
            </span>
          )}
          {unresolved === 0 && !canApply && (
            <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>変更するものはありません</span>
          )}
        </div>
      )}

      {mode === 'class' && (
        <div style={{ borderTop: '1px solid #eee', marginTop: '1rem', paddingTop: '0.7rem' }}>
          <div style={{ fontSize: '0.85rem', color: '#555', marginBottom: '0.3rem' }}>
            正しい年度が今は分からないときは、いったん保留にできます。
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            <input value={holdNote} onChange={e => setHoldNote(e.target.value)}
                   placeholder="メモ（例：2025年度の名簿かもしれない）" style={{ flex: 1, minWidth: 200 }} />
            <button className="btn-secondary" onClick={holdThis} disabled={busy}>保留にする</button>
          </div>
        </div>
      )}
    </div>
  );

  if (!modal) return body;
  return (
    <div role="dialog" aria-modal="true"
         onClick={e => { if (e.target === e.currentTarget && onCancel && !busy) onCancel(); }}
         style={{ position: 'fixed', inset: 0, zIndex: 4300, background: 'rgba(0,0,0,0.35)',
                  display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                  padding: '4vh 16px', overflowY: 'auto' }}>
      {body}
    </div>
  );
}
